import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MinusCircle from "@SupplyLand/MobiusOverrides/MinusCircle";
import parse from "html-react-parser";
import { useState } from "react";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    tabContainer?: TabPresentationProps;
    tabInfoContainer?: TabPresentationProps;
    tabDocumentContainer?: TabPresentationProps;
    tabSpecificationsContainer?: TabPresentationProps;
    hiddenTabContainer?: TabPresentationProps;
    container?: GridContainerProps;
    docContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    tabContent?: TypographyPresentationProps;
    hiddenTabContent?: TypographyPresentationProps;
    documentNameText?: TypographyPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
    tabInfoContainer: {
        css: css`
            text-transform: capitalize;
        `,
    },
    docContainer: {
        css: css`
            & ${GridOffset} {
                display: grid;
            }
        `,
    },
    hiddenTabContainer: {
        css: css`
            display: none;
        `,
    },
    tabContent: {
        css: css`
            cursor: pointer;
            padding-left: 5px;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
            grid-auto-flow: column;
        `,
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsCollapsibleAttributes: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, properties },
}) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    const enum TableTabKeys {
        AdditionalInfo = "Additional Info",
        Specifications = "Specifications",
        Documents = "Documents",
        None = "None",
    }

    const [attributesExpanded, setAttributesTab] = useState(false);
    const [documentsExpanded, setDocumentsTab] = useState(false);
    const [specificationsExpanded, setSpecificationsTab] = useState(false);

    const [currentTab, setCurrentTab] = useState(TableTabKeys.None);
    const [previousTab, unSetTab] = useState(TableTabKeys.AdditionalInfo);

    const onTabChange = (event: React.MouseEvent | React.KeyboardEvent, tabKey?: string) => {
        if (tabKey) {
            setCurrentTab(TableTabKeys.None);
            if (tabKey === TableTabKeys.AdditionalInfo && attributesExpanded === true) {
                setAttributesTab(false);
                setDocumentsTab(false);
                setSpecificationsTab(false);
            }
            if (tabKey === TableTabKeys.Documents && documentsExpanded === true) {
                setDocumentsTab(false);
                setAttributesTab(false);
                setSpecificationsTab(false);
            }
            if (tabKey === TableTabKeys.Specifications && specificationsExpanded === true) {
                setDocumentsTab(false);
                setAttributesTab(false);
                setSpecificationsTab(false);
            }
            if (tabKey === TableTabKeys.AdditionalInfo && attributesExpanded === false) {
                setAttributesTab(true);
                setDocumentsTab(false);
                setSpecificationsTab(false);
            }
            if (tabKey === TableTabKeys.Documents && documentsExpanded === false) {
                setDocumentsTab(true);
                setAttributesTab(false);
                setSpecificationsTab(false);
            }
            if (tabKey === TableTabKeys.Specifications && specificationsExpanded === false) {
                setDocumentsTab(false);
                setAttributesTab(false);
                setSpecificationsTab(true);
            }
        }
    };

    const renderSpecification = (key: string, value: string) => {
        if (key === "customSpecifications") {
            let customContent = value;
            if (customContent.includes("<=")) {
                customContent = customContent.replace(/<=/g, "&lt=");
            }
            if (customContent.includes("=<")) {
                customContent = customContent.replace(/=</g, "=&lt");
            }
            if (customContent.includes(">=")) {
                customContent = customContent.replace(/>=/g, "&gt=");
            }
            if (customContent.includes("=>")) {
                customContent = customContent.replace(/=>/g, "=&gt");
            }
            return <Typography {...styles.tabContent}>{parse(customContent, parserOptions)}</Typography>;
        }
    };
    const renderAttribute = (attributeType: AttributeTypeModel) => {
        if (attributeType.name.toLowerCase() === "countryoforigin") {
            if (attributeType?.attributeValues?.some(attributeValue => attributeValue.value.toLowerCase() === "usa")) {
                return (
                    <Typography
                        key={attributeType.id.toString()}
                        data-test-selector="attributes_item"
                        data-attributetypeid={attributeType.id}
                        {...styles.attributeTypeValuesText}
                        as="div"
                    >
                        <LazyImage
                            src="https://pim-prod20190821211516565500000001.s3.amazonaws.com/5dad09d1-91ff-eb11-b563-2818783e793e/1d1af16f21ce01f_sm.jpg"
                            style={{ marginRight: "1rem" }}
                        />
                    </Typography>
                );
            }
            return <></>;
        }
        if (attributeType.name === "CA_Prop_65") {
            if (attributeType?.attributeValues?.some(attributeValue => attributeValue.value.toLowerCase() === "yes")) {
                return (
                    <Link key={attributeType.id} href="http://www.p65warnings.ca.gov" target="_blank">
                        <LazyImage
                            height="40px"
                            src="https://pim-prod20190821211516565500000001.s3.amazonaws.com/5dad09d1-91ff-eb11-b563-2818783e793e/c4ac890e30cee87_md.png"
                            style={{ marginRight: "1rem" }}
                        />
                    </Link>
                );
            }
            return <></>;
        }
    };

    const createAttributes = (attributeTypes: AttributeTypeModel[] | undefined): AttributeTypeModel[] => {
        if (!attributeTypes) {
            return [] as AttributeTypeModel[];
        }
        const attributes: AttributeTypeModel[] = [];

        attributeTypes.forEach(attributeType => {
            if (
                attributeType.name.toLowerCase() !== "ca_prop_65" &&
                attributeType.name.toLowerCase() !== "countryoforigin"
            ) {
                attributes.push(attributeType);
            }
        });
        const imageAttributes: AttributeTypeModel[] = [];
        attributeTypes.forEach(attributeType => {
            if (
                (attributeType.name.toLowerCase() === "ca_prop_65" &&
                    attributeType?.attributeValues?.some(
                        attributeValue => attributeValue.value.toLowerCase() === "yes",
                    )) ||
                (attributeType.name.toLowerCase() === "countryoforigin" &&
                    attributeType?.attributeValues?.some(
                        attributeValue => attributeValue.value.toLowerCase() === "usa",
                    ))
            ) {
                attributes.push(attributeType);
            }
        });
        return attributes;
    };

    const allAttributes: AttributeTypeModel[] = createAttributes(attributeTypes);
    const renderingSpecifications = specifications && specifications.length > 0;

    return (
        <TabGroup current={currentTab} onTabChange={onTabChange}>
            <Tab
                headline={
                    <Typography {...styles.tabContent}>
                        <Icon width={15} height={15} src={attributesExpanded ? MinusCircle : PlusCircle} />
                        <Typography {...styles.tabContent}>{translate(TableTabKeys.AdditionalInfo)}</Typography>
                    </Typography>
                }
                key={TableTabKeys.AdditionalInfo}
                tabKey={TableTabKeys.AdditionalInfo}
            >
                {attributesExpanded &&
                properties["customSpecifications"] &&
                properties["customSpecifications"].length > 0 ? (
                    <Typography {...styles.tabContent}>
                        {Object.entries(properties).map(([key, value]) => {
                            return renderSpecification(key, value);
                        })}
                    </Typography>
                ) : (
                    <></>
                )}
                {attributesExpanded ? (
                    <Typography {...styles.tabContent}>
                        {allAttributes.map(attributeType => {
                            return renderAttribute(attributeType);
                        })}
                    </Typography>
                ) : (
                    <></>
                )}
            </Tab>
            <Tab
                {...styles.tabSpecificationsContainer}
                style={specifications === undefined || specifications?.length === 0 ? { display: "none" } : {}}
                headline={
                    <Typography {...styles.tabContent}>
                        <Icon width={15} height={15} src={specificationsExpanded ? MinusCircle : PlusCircle} />
                        <Typography {...styles.tabContent}>{translate(TableTabKeys.Specifications)}</Typography>
                    </Typography>
                }
                key={TableTabKeys.Specifications}
                tabKey={TableTabKeys.Specifications}
            >
                {specificationsExpanded && renderingSpecifications ? (
                    specifications?.map((specification, index) => {
                        let htmlContent = specification.htmlContent;
                        if (htmlContent.includes("<=")) {
                            htmlContent = htmlContent.replace(/<=/g, "&lt=");
                        }
                        if (htmlContent.includes("=<")) {
                            htmlContent = htmlContent.replace(/=</g, "=&lt");
                        }
                        if (htmlContent.includes(">=")) {
                            htmlContent = htmlContent.replace(/>=/g, "&gt=");
                        }
                        if (htmlContent.includes("=>")) {
                            htmlContent = htmlContent.replace(/=>/g, "=&gt");
                        }
                        return <Typography key={index} {...styles.tabContent}>{parse(htmlContent, parserOptions)}</Typography>;
                    })
                ) : (
                    <></>
                )}
            </Tab>
            <Tab
                {...styles.tabDocumentContainer}
                style={documents === undefined || documents?.length === 0 ? { display: "none" } : {}}
                headline={
                    <Typography {...styles.tabContent}>
                        <Icon width={15} height={15} src={documentsExpanded ? MinusCircle : PlusCircle} />
                        <Typography {...styles.tabContent}>{translate(TableTabKeys.Documents)}</Typography>
                    </Typography>
                }
                key={TableTabKeys.Documents}
                tabKey={TableTabKeys.Documents}
            >
                {documentsExpanded ? (
                    <StyledWrapper {...styles.wrapper}>
                        <GridContainer {...styles.docContainer}>
                            {documents?.map(document => (
                                <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                                    <Link
                                        href={document.filePath}
                                        target="_new"
                                        {...styles.documentLink}
                                        data-test-selector={`productDetails_${document.id}`}
                                    >
                                        <IconMemo {...styles.documentIcon} />
                                        <Typography {...styles.documentNameText}>{document.name}</Typography>
                                    </Link>
                                </GridItem>
                            ))}
                        </GridContainer>
                    </StyledWrapper>
                ) : (
                    <></>
                )}
            </Tab>
            <Tab
                {...styles.hiddenTabContainer}
                headline={translate(TableTabKeys.None)}
                key={TableTabKeys.None}
                tabKey={TableTabKeys.None}
                hidden={true}
            >
                <></>
            </Tab>
        </TabGroup>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsCollapsibleAttributes),
    definition: {
        displayName: "Collapsible Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
